// 匯出來源（vms-web §5.8 EXP-01）：cloud 雲端片段、local NVR / Kinio Station 硬碟（設備經 ingest 上傳）、backup 異地備份副本。

export type ExportSourceKind = 'cloud' | 'local' | 'backup';

export const EXPORT_SOURCES: ExportSourceKind[] = ['cloud', 'local', 'backup'];

/** 本地來源只收 NVR / Station 的通道（<parent>:chN）；攝影機的 SD 還沒有索引 RPC。 */
export function canExportFrom(source: ExportSourceKind, aid: string): boolean {
  return source !== 'local' || /.:ch\d+$/.test(aid);
}

const CODES = [
  'local_export_unsupported',
  'local_upload_timeout',
  'local_export_failed',
  'no_recording',
  'codec_not_supported',
  'signing_failed',
  'queue_timeout',
] as const;

/**
 * 不能建新匯出的原因（伺服器 403 EXPORT_NOT_ENABLED / 503 EXPORT_UNAVAILABLE 的 detail.reason / 409 EXPORT_QUOTA_EXCEEDED，
 * 或列表的 create.reason）→ i18n `exports.block_<reason>`。
 */
const BLOCK_REASONS = ['not_enabled', 'worker_disabled', 'tmp_space_low', 'gateway_unreachable', 'quota_exceeded'] as const;
export type BlockReason = (typeof BLOCK_REASONS)[number];

export function blockReason(code: string, detail: Record<string, unknown> | undefined): BlockReason | null {
  if (code === 'EXPORT_NOT_ENABLED') return 'not_enabled';
  if (code === 'EXPORT_QUOTA_EXCEEDED') return 'quota_exceeded'; // 組織免費總量用完（SPEC #vms-web-export-archive）
  if (code !== 'EXPORT_UNAVAILABLE') return null;
  const r = detail?.reason;
  return BLOCK_REASONS.find((x) => x === r) ?? 'gateway_unreachable';
}

/** 工作的 error 以代碼開頭的，回代碼（給 i18n `exports.fail_<code>`）；其他回 null（原文照顯示）。 */
export function failCode(error: string | undefined): (typeof CODES)[number] | null {
  if (!error) return null;
  return CODES.find((c) => error.startsWith(c)) ?? null;
}

/** 一個工作用到的來源（去重、照 EXPORT_SOURCES 的順序）。沒寫 = cloud。 */
export function sourcesOf(sources: { source?: string }[]): ExportSourceKind[] {
  const set = new Set(sources.map((s) => (s.source || 'cloud') as ExportSourceKind));
  return EXPORT_SOURCES.filter((k) => set.has(k));
}

/** 網址 src= → 匯出來源（認不得 = 沒指定）。 */
export function srcOf(v: string | null | undefined): ExportSourceKind | undefined {
  return EXPORT_SOURCES.find((k) => k === v);
}

/**
 * 新增匯出的來源預設（SPEC #vms-web-540，VMS UX 評審 2026-10-13 P0-6 ⑥）：這段時間實際有錄影的來源——
 * 雲端有就雲端，否則 NVR / Kinio Station 硬碟（只有通道能從本地匯出）；都不是就仍是雲端（欄位下方說沒有錄影）。
 */
export function defaultSource(cloudHasRecording: boolean, aid: string): ExportSourceKind {
  if (cloudHasRecording) return 'cloud';
  return canExportFrom('local', aid) ? 'local' : 'cloud';
}
